/**
* @summary - Component that Provides Work Group Listing Page
* @author Yuvasri S
* @since 2024-05-28
*/
import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import CustomSelectInput from '../commoncomponents/CustomSelectInput';
import CustomDialog from '../commoncomponents/CustomDialog';
import { useNavigate } from 'react-router-dom';
import { DefaultCard } from '../../assets/styled-components/card';
import { getAuthToken, getFacilityId } from "../../utility/tokenUtility";
import axios from "axios";
import CreateNewButton from '../commoncomponents/CreateNewButton';
import RefreshExportButtons from '../commoncomponents/RefreshExportButtons';
import { FemsGraph, BemsGraph, MasterGraph } from "../../utility/apiURLS";
import CloseButton from '../commoncomponents/CloseButton';
import PageTitle from '../commoncomponents/PageTitle';
import SectionTitle from '../commoncomponents/SectionTitle';
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';


const columns = [
  { field: 'wgcode', headerName: 'Work Group Code', width: 180 },
  { field: 'wgdesc', headerName: 'Work Group Description', width: 150 },
];

const WorkGroupList = () => {
  const [rows, setRows] = useState([]);
  const [femsData, setFemsData] = useState([]);
  const [bemsData, setBemsData] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [deletedRowDetails, setDeletedRowDetails] = useState({});
  const navigate = useNavigate();
  const [gridApi, setGridApi] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [filterModel, setFilterModel] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportCount, setExportCount] = useState(0)
  const serviceOptions = [
    { label: "FEMS", value: "FEMS", id: 1 },
    { label: "BEMS", value: "BEMS", id: 2 },
  ];
  const [service, setService] = useState(serviceOptions[0]);

  const accessData = useAccess();
  const access =  utilities.getScreenPermissions(accessData, "ENGM003");



  const token = getAuthToken("id_token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const facilityId = getFacilityId("facilityId");

  const fetchInitialData = async () => {
    setIsLoading(true);
    setRows([]);
    try {
      let femsResponse, bemsResponse;
      // Fetch data based on the selected service (FEMS or BEMS)
      if (service.value === "FEMS") {
        femsResponse = await axios.get(`${FemsGraph}/api/WorkGroupDetails/GetWorkGroupDetailsList/${facilityId}?page=${pageIndex}&size=${pageSize}&filterOptions=${JSON.stringify(
          filterModel
        )}&sortOptions=${JSON.stringify(sortModel)}`, config);
        setRowCount(femsResponse.data.totalCount);
        setExportCount(femsResponse.data.totalCount);
        setFemsData(femsResponse.data.items);
        setRows(femsResponse.data.items);
      } else if (service.value === "BEMS") {
        bemsResponse = await axios.get(`${BemsGraph}/api/WorkGroupDetails/GetWorkGroupDetailsList/${facilityId}?page=${pageIndex}&size=${pageSize}&filterOptions=${JSON.stringify(
          filterModel
        )}&sortOptions=${JSON.stringify(sortModel)}`, config);
        setRowCount(bemsResponse.data.totalCount);
        setExportCount(bemsResponse.data.totalCount);
        setBemsData(bemsResponse.data.items);
        setRows(bemsResponse.data.items);
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
    }finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, [service, filterModel, sortModel, pageIndex, pageSize]);

  const handleServiceChange = (selectedService) => {
    setService(selectedService);
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([]);
  };

  const fetchExportdata = async () => {
    setIsLoading(true);

    try {
      let femsResponse, bemsResponse;

      // Fetch data based on the selected service (FEMS or BEMS)
      if (service.value === "FEMS") {
        femsResponse = await axios.get(`${FemsGraph}/api/WorkGroupDetails/GetWorkGroupExport/${facilityId}? filterOptions=${JSON.stringify(
          filterModel
        )}&sortOptions=${JSON.stringify(sortModel)}`, config);
        setExportData(femsResponse.data)

      } else if (service.value === "BEMS") {
        bemsResponse = await axios.get(`${BemsGraph}/api/WorkGroupDetails/GetWorkGroupExport/${facilityId}? filterOptions=${JSON.stringify(
          filterModel
        )}&sortOptions=${JSON.stringify(sortModel)}`, config);
        setExportData(bemsResponse.data)

      }

    } catch (error) {
      console.error("Error fetching initial data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (!rowToDelete || !rowToDelete.wgId) {
        console.error("Row to delete or its id is undefined");
        return;
      }

      const isFems = rowToDelete.serviceName === 'FEMS';
      const deleteURL = isFems
        ? `${FemsGraph}/api/femworkgroupdetails/${rowToDelete.wgId}`
        : `${BemsGraph}/api/bemworkgroupdetails/${rowToDelete.wgId}`;

      await axios.delete(deleteURL, config);
      const updatedRows = rows.filter((row) => row.wgId !== rowToDelete.wgId);
      setRows(updatedRows);
      setFemsData(femsData.filter((row) => row.wgId !== rowToDelete.wgId));
      setBemsData(bemsData.filter((row) => row.wgId !== rowToDelete.wgId));

      setDeletedRowDetails({ code: rowToDelete.wgcode, desc: rowToDelete.wgdesc });

      // Close the delete confirmation dialog and open the success dialog
      setDeleteDialogOpen(false);  // Ensure this closes the delete confirmation dialog
      setConfirmationDialogOpen(true);
      setRowToDelete(null);
    } catch (error) {
      console.error("Error deleting work group:", error);
      if (error.response && error.response.status === 400) {
        setErrorMessage("Record cannot be deleted as it is referred in another record.");
      } else {
        setErrorMessage("An error occurred while deleting the Work Group. Please try again.");
      }
      setDeleteDialogOpen(false);  // Close the delete dialog on error as well
    }
  };

  const handleCreateWorkGroup = () => {
    navigate("register");
  };

  const handleEdit = (rowData) => {
    navigate("register", { state: { ...rowData, readOnlyEdit: true, mode: 'edit' } });
  };

  const handleView = (rowData) => {
    navigate("register", { state: { ...rowData, readOnly: true, mode: 'view' } });
  };

  const handleRefresh = () => {
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([])
  };

  return (
    <>
      <PageTitle title="Work Group" />
      <DefaultCard>
        <Box
          mb={1}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <SectionTitle title="Work Group List" />
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <CustomSelectInput
              fullWidth={false}
              labelSize={"14px"}
              sx={{ width: 150 }}
              label={"Service"}
              options={serviceOptions}
              value={service}
              onChange={handleServiceChange}
              placeholder={"Select Service"}
              highlight={true}
            />
            {access?.canCreate && <CreateNewButton onClick={handleCreateWorkGroup} />}

            <RefreshExportButtons
              exportFunction={fetchExportdata}
              exportData={exportData}
              refData={gridApi}
              handleRefresh={handleRefresh}
              title={"Work Group"}
            />
          </Stack>
        </Box>

        <CustomAgGrid
          rows={rows}
          columns={columns}
          handleEdit={access?.canEdit && handleEdit}
          handleDelete={access?.canDelete &&handleDelete}
          handleView={access?.canView &&handleView}
          ref={gridApi}
          setGridApi={setGridApi}
          isLoading={isLoading}
          pagination={false}
          setFilterModel={setFilterModel}
          setSortModel={setSortModel}
          totalCount={rowCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
        />
      </DefaultCard>
      <CustomDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Confirmation!"
        content="Are you sure you want to delete?"
        cancelBtn='No'
        confirmBtn="Yes"
      />
      <CustomDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        title="Deletion Successful"
        content="Record deleted successfully."
        confirmBtn="OK"
        onConfirm={() => setConfirmationDialogOpen(false)}
      />
      <CustomDialog
        open={!!errorMessage}
        onClose={() => setErrorMessage("")}
        onConfirm={() => setErrorMessage("")}
        content={errorMessage}
        confirmBtn="Ok"
      />
      <CloseButton />
    </>
  );
};

export default WorkGroupList;
